import React, { useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import BarcodeInput from "../components/BarcodeInput";
import CameraScanner from "../components/CameraScanner";
import LabelPhotoScanner from "../components/LabelPhotoScanner";
import { parseBarcodeLabel } from "../lib/barcodeLabel";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Camera, ScanSearch, Trash2, ArrowUpFromLine, AlertTriangle } from "lucide-react";

export default function BulkExit() {
  const { user } = useAuth();
  const isSupervisor = user?.role === "supervisor";
  const [musteri, setMusteri] = useState("");
  const [proje, setProje] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [rows, setRows] = useState([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [labelScannerOpen, setLabelScannerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleScan = useCallback(
    async (raw) => {
      const parsed = parseBarcodeLabel(raw);
      const barkod = String(parsed.barkod || "").trim();
      if (!barkod) return { ok: false };

      // if already in the list — barcode already added, do nothing (whole stock already selected)
      const existingIdx = rows.findIndex((r) => r.barkod === barkod);
      if (existingIdx >= 0) {
        toast.info(`Zaten fişte · ${barkod}`);
        return { ok: true };
      }

      try {
        const { data } = await api.get(
          `/inventory/by-barcode/${encodeURIComponent(barkod)}`,
        );
        if (!data.found) {
          toast.error(`Barkod bulunamadı: ${barkod}`);
          return { ok: false };
        }
        if (data.item.adet <= 0) {
          toast.error(`Stokta yok: ${barkod}`);
          return { ok: false };
        }
        const pend = data.pending_exit;
        const available = data.item.adet - (pend?.adet || 0);
        if (pend && available <= 0) {
          toast.error(
            `Bu cam onaya gönderildi, tekrar düşemezsiniz · ${barkod} (talep eden: ${pend.requester})`,
            { duration: 7000 },
          );
          return { ok: false };
        }
        if (pend) {
          toast.warning(`${barkod}: ${pend.adet} adet zaten onay bekliyor · en fazla ${available} adet çıkış yapılabilir`, {
            duration: 7000,
          });
        }
        // Bulk exit default: take the ENTIRE stock at that barcode. User can
        // reduce via +/- buttons if a partial exit is intended.
        setRows((prev) => [
          {
            barkod,
            adet: available,
            isim: data.item.isim,
            ebat: data.item.ebat,
            sehpa_konumu: data.item.sehpa_konumu,
            currentAdet: available,
          },
          ...prev,
        ]);
        if (!pend) toast.success(`Bulundu: ${data.item.isim || barkod} · ${available} adet seçildi`);
        return { ok: true };
      } catch (e) {
        toast.error(formatApiError(e));
        return { ok: false };
      }
    },
    [rows],
  );

  const removeRow = (barkod) =>
    setRows((prev) => prev.filter((r) => r.barkod !== barkod));

  const changeAdet = (barkod, delta) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.barkod !== barkod) return r;
        const next = Math.max(1, Math.min(r.currentAdet, r.adet + delta));
        return { ...r, adet: next };
      }),
    );

  const submit = async () => {
    if (rows.length === 0) {
      toast.error("En az bir ürün ekleyin.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        musteri,
        proje,
        aciklama,
        items: rows.map((r) => ({ barkod: r.barkod, adet: r.adet })),
      };
      const { data } = await api.post(
        isSupervisor ? "/exits/bulk" : "/approvals/exit",
        body,
      );
      if (isSupervisor) {
        const okCount = data.results.length;
        const errCount = data.errors?.length || 0;
        toast.success(
          `Fiş #${data.fis_id} · ${okCount} çıkış${errCount ? `, ${errCount} hata` : ""}`,
        );
        if (errCount) {
          data.errors.forEach((e) =>
            toast.error(`${e.barkod}: ${e.error}`, { duration: 6000 }),
          );
        }
        if (okCount > 0) {
          setRows([]);
          setMusteri("");
          setProje("");
          setAciklama("");
        }
      } else {
        toast.success(
          `Çıkış talebi oluşturuldu · ${data.count} kayıt Supervisor onayı bekliyor`,
        );
        setRows([]);
        setMusteri("");
        setProje("");
        setAciklama("");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const totalAdet = rows.reduce((a, r) => a + Number(r.adet || 0), 0);

  return (
    <div className="space-y-6">
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5">
        <div className="ticker text-[color:var(--text-3)] mb-4">
          /// ÇIKIŞ FİŞİ · MÜŞTERİYE / PROJEYE
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="ticker block mb-2 text-[color:var(--text-2)]">
              Müşteri
            </label>
            <input
              data-testid="bulk-exit-musteri"
              value={musteri}
              onChange={(e) => setMusteri(e.target.value)}
              placeholder="Müşteri adı"
              className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--accent)] outline-none"
            />
          </div>
          <div>
            <label className="ticker block mb-2 text-[color:var(--text-2)]">
              Proje
            </label>
            <input
              data-testid="bulk-exit-proje"
              value={proje}
              onChange={(e) => setProje(e.target.value)}
              placeholder="Proje / iş adı"
              className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--accent)] outline-none"
            />
          </div>
          <div>
            <label className="ticker block mb-2 text-[color:var(--text-2)]">
              Açıklama
            </label>
            <input
              data-testid="bulk-exit-note"
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              placeholder="Fiş açıklaması (opsiyonel)"
              className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--accent)] outline-none"
            />
          </div>
        </div>
      </div>

      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="ticker text-[color:var(--text-3)]">
            /// BARKOD OKUT (ÇIKIŞ)
          </div>
          <div className="flex gap-2">
            <button
              data-testid="bulk-exit-label-scanner-btn"
              onClick={() => setLabelScannerOpen(true)}
              className="flex items-center gap-2 px-3 py-2 hard border-2 border-[color:var(--border)] hover:border-[color:var(--primary)] hover:text-[color:var(--primary)] transition-colors text-sm font-semibold"
              title="Fotoğraf çek — etiketin tamamını oku"
            >
              <ScanSearch size={16} /> Etiket OCR
            </button>
            <button
              data-testid="bulk-exit-camera-btn"
              onClick={() => setCameraOpen(true)}
              className="flex items-center gap-2 px-3 py-2 hard border-2 border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors text-sm font-semibold"
            >
              <Camera size={16} /> Kameradan Okut
            </button>
          </div>
        </div>
        <BarcodeInput
          onScan={handleScan}
          disabled={submitting}
          testId="bulk-exit-barcode-input"
        />
      </div>

      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)]">
        <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
          <div>
            <h3 className="font-bold">Fiş Kalemleri</h3>
            <div className="ticker text-[color:var(--text-3)]">
              {rows.length} kalem · Toplam {totalAdet} adet çıkacak
            </div>
          </div>
          <div className="flex gap-2">
            <button
              disabled={rows.length === 0 || submitting}
              onClick={() => setRows([])}
              data-testid="bulk-exit-clear"
              className="px-4 py-2 hard border-2 border-[color:var(--border)] hover:border-[color:var(--error)] hover:text-[color:var(--error)] disabled:opacity-40 transition-colors text-sm font-semibold"
            >
              Temizle
            </button>
            <button
              disabled={rows.length === 0 || submitting}
              onClick={submit}
              data-testid="bulk-exit-submit"
              className="px-6 py-2 hard bg-[color:var(--accent)] hover:bg-[color:var(--accent-hover)] text-white font-bold uppercase tracking-wider disabled:opacity-40 transition-colors flex items-center gap-2"
            >
              <ArrowUpFromLine size={16} strokeWidth={2.5} />
              {submitting ? "İşleniyor…" : "Çıkışı Onayla"}
            </button>
          </div>
        </div>
        <div className="overflow-auto" style={{ maxHeight: "50vh" }}>
          <table className="dense-table w-full">
            <thead>
              <tr>
                <th>Barkod</th>
                <th>İsim</th>
                <th>Ebat</th>
                <th>Sehpa</th>
                <th>Çıkış Adet</th>
                <th>Mevcut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-[color:var(--text-3)] py-12">
                    Fiş boş. Yukarıdan barkod okutmaya başlayın.
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const overflow = r.adet > r.currentAdet;
                return (
                  <tr
                    key={r.barkod}
                    className="row-enter"
                    data-testid="bulk-exit-row"
                  >
                    <td className="font-mono font-semibold">{r.barkod}</td>
                    <td className="max-w-xs truncate">{r.isim}</td>
                    <td className="font-mono text-[color:var(--text-2)]">
                      {r.ebat}
                    </td>
                    <td>
                      <span className="inline-block px-2 py-0.5 hard bg-[color:var(--bg)] font-mono text-xs font-semibold">
                        {r.sehpa_konumu || "-"}
                      </span>
                    </td>
                    <td>
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => changeAdet(r.barkod, -1)}
                          className="w-6 h-6 hard border border-[color:var(--border)] hover:bg-[color:var(--bg)] font-mono"
                        >
                          −
                        </button>
                        <span
                          className={`font-mono font-bold px-2 min-w-[2ch] text-center ${
                            overflow ? "text-[color:var(--error)]" : ""
                          }`}
                        >
                          {r.adet}
                        </span>
                        <button
                          onClick={() => changeAdet(r.barkod, 1)}
                          className="w-6 h-6 hard border border-[color:var(--border)] hover:bg-[color:var(--bg)] font-mono"
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td className="text-[color:var(--text-3)] text-xs font-mono">
                      {r.currentAdet} → {Math.max(0, r.currentAdet - r.adet)}
                      {overflow && (
                        <AlertTriangle
                          size={12}
                          className="inline ml-1 text-[color:var(--error)]"
                        />
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => removeRow(r.barkod)}
                        className="p-1.5 hard hover:bg-[color:var(--error-bg)] hover:text-[color:var(--error)] transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CameraScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onScan={(code) => handleScan(code)}
      />

      <LabelPhotoScanner
        open={labelScannerOpen}
        onClose={() => setLabelScannerOpen(false)}
        onExtract={(ex) => {
          if (ex?.barkod) handleScan(ex.barkod);
          else toast.error("Etiketten barkod alınamadı.");
        }}
      />
    </div>
  );
}
